'use client';

import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { scrollMarketingSection } from '@/components/website/Marketing/marketing-scroll';
import {
  MARKETING,
  MARKETING_HERO_SECTION_SX,
} from '@/components/website/Marketing/marketing-tokens';
import MarketingSectionImage from './MarketingSectionImage';
import { resolvedImageAlt, resolvedImageUrl } from './marketing-page-utils';

const REDUCED_MOTION_SX = {
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
};

const FADE_UP_KEYFRAMES = {
  '@keyframes marketingPageHeroFadeUp': {
    from: { opacity: 0, transform: 'translateY(18px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

const SCROLL_IN_KEYFRAMES = {
  '@keyframes marketingPageHeroScrollIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
};

const SCROLL_BOUNCE_KEYFRAMES = {
  '@keyframes marketingPageHeroScrollBounce': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(8px)' },
  },
};

export default function MarketingPageHero({ data, scrollTargetId }) {
  const imageUrl = resolvedImageUrl(data?.imageAssetId ?? data?.image);
  const imageAlt = resolvedImageAlt(data?.imageAssetId ?? data?.image, data?.headline || 'NIICO');
  const eyebrow = data?.eyebrow || 'Our Brand';

  const handleScrollCue = event => {
    event.preventDefault();
    if (scrollTargetId) scrollMarketingSection(scrollTargetId);
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        ...MARKETING_HERO_SECTION_SX,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {imageUrl ? (
        <MarketingSectionImage
          src={imageUrl}
          alt={imageAlt}
          sizes="100vw"
          priority
          objectPosition="left center"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        />
      ) : (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: MARKETING.ink }} />
      )}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: [
            'linear-gradient(90deg, rgba(30,25,21,.82) 0%, rgba(30,25,21,.42) 38%, rgba(30,25,21,.08) 68%, transparent 100%)',
            'linear-gradient(180deg, rgba(30,25,21,.35) 0%, transparent 28%, rgba(30,25,21,.55) 100%)',
          ].join(', '),
        }}
      />
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pt: { xs: '24px', md: '40px' },
          px: 'clamp(44px, 6vw, 88px)',
          pb: '28px',
        }}
      >
        <Box sx={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <Typography
            sx={{
              ...FADE_UP_KEYFRAMES,
              fontFamily: MARKETING.sans,
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: 1,
              letterSpacing: '0.42em',
              textTransform: 'uppercase',
              color: 'rgba(239, 232, 220, 0.7)',
              animation: `marketingPageHeroFadeUp 0.8s ${MARKETING.ease} both`,
              ...REDUCED_MOTION_SX,
            }}
          >
            {eyebrow}
          </Typography>
          <Typography
            component="h1"
            sx={{
              ...FADE_UP_KEYFRAMES,
              m: 0,
              fontFamily: MARKETING.beVietnam,
              fontWeight: 700,
              fontSize: 'clamp(56px, 8vw, 96px)',
              lineHeight: 0.9,
              letterSpacing: '0.22em',
              color: MARKETING.text,
              animation: `marketingPageHeroFadeUp 0.9s ${MARKETING.ease} 0.12s both`,
              ...REDUCED_MOTION_SX,
            }}
          >
            {data?.headline || 'NIICO'}
          </Typography>
          {data?.tagline ? (
            <Typography
              component="p"
              sx={{
                ...FADE_UP_KEYFRAMES,
                m: 0,
                maxWidth: 440,
                fontFamily: MARKETING.serif,
                fontWeight: 300,
                fontSize: 'clamp(20px, 2.4vw, 26px)',
                lineHeight: 1.35,
                color: MARKETING.text,
                textWrap: 'pretty',
                animation: `marketingPageHeroFadeUp 0.95s ${MARKETING.ease} 0.28s both`,
                ...REDUCED_MOTION_SX,
              }}
            >
              {data.tagline}
            </Typography>
          ) : null}
          {data?.subheadline ? (
            <Typography
              component="p"
              sx={{
                ...FADE_UP_KEYFRAMES,
                m: '8px 0 0',
                maxWidth: 520,
                fontFamily: MARKETING.sans,
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: 1.8,
                color: 'rgba(239, 232, 220, 0.78)',
                textWrap: 'pretty',
                animation: `marketingPageHeroFadeUp 1s ${MARKETING.ease} 0.45s both`,
                ...REDUCED_MOTION_SX,
              }}
            >
              {data.subheadline}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {scrollTargetId ? (
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            px: '44px',
            pb: '36px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            component="a"
            href={`#${scrollTargetId}`}
            onClick={handleScrollCue}
            sx={{
              ...SCROLL_IN_KEYFRAMES,
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              fontFamily: MARKETING.sans,
              fontWeight: 400,
              fontSize: '10px',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color: 'rgba(239, 232, 220, 0.62)',
              textDecoration: 'none',
              cursor: 'pointer',
              animation: 'marketingPageHeroScrollIn 0.9s ease 0.9s both',
              '&:hover': { color: MARKETING.text },
              ...REDUCED_MOTION_SX,
            }}
          >
            <span>Scroll</span>
            <Box
              component="span"
              aria-hidden
              sx={{
                ...SCROLL_BOUNCE_KEYFRAMES,
                display: 'block',
                fontSize: '14px',
                lineHeight: 1,
                animation: 'marketingPageHeroScrollBounce 1.6s ease-in-out 1.8s infinite',
                ...REDUCED_MOTION_SX,
              }}
            >
              ↓
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

MarketingPageHero.propTypes = {
  data: PropTypes.object,
  scrollTargetId: PropTypes.string,
};
